var vsprintf = require('sprintf-js').vsprintf;

var Config = require('../config/Config');
var Constants = require('../constants/Constants');
var UserStore = require('../stores/UserStore');
var encryptValue = require('../util/eCrypt');

function buildHmacString (_hmac) {
    return vsprintf('HMAC %s:%s', [_hmac.user, _hmac.hmac]);
}

var Base = require('./Base');
var Api = new Base({
    base_url: Config.invocationApiUrl
});
Api.createRequest = function(method, url, query, params, customHeaders) {
    var req = Base.prototype.createRequest.call(this, method, url, query, params, customHeaders);

    if (!req.header.hasOwnProperty('Authorization') && UserStore.hasData()) {
        var _hmac = UserStore.getHmac();
        req.set('Authorization', buildHmacString(_hmac));
        req.set('X-HMAC-Expires', _hmac.expires);
    }

    req.set('Invocation-Serialize-Null', 'false');

    return req;
};

var Invocation = {
    /**
     * Authentication related API calls
     */
    auth: {
        auth: function (clientId, username, roles, firstName, lastName, contactNumber) {
          var url = Api.makeUrl('/relationship/%s', [clientId]);

          return Api.sendAndDispatchPromise(Constants.auth_api.AUTH, 'PUT', url, null, {
            username,
            firstName,
            lastName,
            contactNumber,
            roles,
          });
        },

        deauth: function (clientId, username, replaceWith) {
          var url = Api.makeUrl('/relationship/%s', [clientId]);

          return Api.sendAndDispatchPromise(Constants.auth_api.DEAUTH, 'DELETE', url, null, {
            username,
            replaceWith,
          });
        },

      modifyAuth: function (clientId, username, roles) {
        var url = Api.makeUrl('/relationship/%s', [clientId]);

        return Api.sendAndDispatchPromise(Constants.auth_api.MODIFY_AUTH, 'POST', url, null, {
          username,
          roles
        });
      },

        /**
         * Retrieves a new HMAC provided the existing HMAC is not too old.
         */
        refreshHmac: function () {
            var url = Api.makeUrl('/auth/hmac');

            Api.send(Constants.auth_api.REFRESH_HMAC, 'POST', url);
        }
    },

    bandwidth: {
        /**
         * Retrieves bandwidth for the given IP addresses.
         *
         * Does not dispatch.
         */
        getCurrentBandwidth: function (service, ips, monthiversary, start, end) {
            var url = Api.makeUrl('/usage/service/month/%s', [service.id]);

            return Api.sendPromise(Constants.api.UPDATE_BANDWIDTH_CURRENT, 'POST', url, null, {
                end: end,
                anniversary: monthiversary,
                start: start
            });
        },

        /**
         * Retrieves bandwidth for the given IP addresses.
         *
         * Does not dispatch.
         */
        getHistoricalBandwidth: function (service, ips, monthiversary, start, end) {
            var url = Api.makeUrl('/usage/service/day/%s', [service.id]);

            return Api.sendPromise(Constants.api.UPDATE_BANDWIDTH_HISTORY, 'POST', url, null, {
                end: end,
                anniversary: monthiversary,
                start: start
            });
        }
    },

    /**
     * Billing related API
     */
    billing: {
        /**
         * Creates a new token for a given credit card.
         */
        addToken: function (clientId, card, billingGroupDefaults) {
            const url = Api.makeUrl('/payment/token/create/%s', [clientId]);

            card.number = encryptValue(card.number, Config.eCryptKey);
            card.cvn = encryptValue(card.cvn, Config.eCryptKey);

            return Api.sendPromise(Constants.api.CREATE_TOKEN, 'POST', url, null, {
                billing_group_defaults: billingGroupDefaults,
                card
            });
        },

        /**
         * Deletes a token.
         */
        deleteToken: function (token) {
            var url = Api.makeUrl('/payment/token/%s', [token.uuid]);

            Api.send(Constants.api.DELETE_TOKEN, 'DELETE', url, null, null, { token: token });
        },

        /**
         * Takes a payment from a given card while optionally saving the token.
         */
        payFromCard: function (card, amount, clientId, save) {
            var url = Api.makeUrl('/payment/card/%s', [clientId]);

            card.number = encryptValue(card.number, Config.eCryptKey);
            card.cvn = encryptValue(card.cvn, Config.eCryptKey);

            return Api.sendPromise(Constants.api.PAY_CARD, 'POST', url, null, {
                amount: amount,
                card: card,
              save
            });
        },

        /**
         * Takes a payment from a provided token.
         */
        payFromToken: function (token, amount, cvn, save) {
            var url = Api.makeUrl('/payment/token/%s', [token.uuid]);

            cvn = encryptValue(cvn, Config.eCryptKey);

            return Api.sendPromise(Constants.api.PAY_TOKEN, 'POST', url, null, {
                amount: amount,
                cvn: cvn,
              save,
            });
        },

        /**
         * Updates a given billing group's token to the new value.
         */
        updateToken: function (billingGroup, token) {
            if (!token) {
                return Api.sendPromise(
                    Constants.api.UPDATE_TOKEN,
                    'DELETE',
                    Api.makeUrl('/incontrol/billing_group/%s/token', [billingGroup.id])
                );
            }

            return Api.sendPromise(
                Constants.api.UPDATE_TOKEN,
                'POST',
                Api.makeUrl('/incontrol/billing_group/%s/token/%s', [billingGroup.id, token.uuid])
            );
        }
    },

    /**
     * API calls related to a ClientService
     */
    clientService: {
        /**
         * Changes an email account password for an account that belongs to the supplied
         * service.
         */
        changeEmailPassword: function (service, email, newPassword) {
            var url = Api.makeUrl('/incontrol/client_service/%s/email/password', [service.id]);

            return Api.sendAndDispatchPromise(Constants.api.SERVICE_EMAIL_PASSWORD, 'POST', url, null, {
                email_address: email,
                new_password: newPassword
            }, { service: service });
        },

        /**
         * Changes a service password.
         */
        changeServicePassword: function (service, newPassword) {
            var url = Api.makeUrl('/incontrol/client_service/%s/password', [service.id]);

            return Api.sendPromise(Constants.api.SERVICE_PASSWORD, 'POST', url, null, {
                password: newPassword
            }, { service: service });
        },

        checkUsername: function (username, serviceType) {
            var url = Api.makeUrl('/incontrol/username');

            return Api.sendPromise(Constants.api.CHECK_USERNAME, 'POST', url, null, {
                service_type: serviceType,
                username: username
            }, { Authorization: '' });
        },

        /**
         * Creates a new email belonging to the supplied service.
         */
        createEmail: function (service, email, password) {
            var url = Api.makeUrl('/incontrol/client_service/%s/email/create', [service.id]);

            return Api.sendAndDispatchPromise(Constants.api.SERVICE_EMAIL_CREATE, 'POST', url, null, {
                email_address: email,
                password: password
            }, { service: service });
        },

        /**
         * Deletes an email account belonging to a supplied service.
         */
        deleteEmail: function (service, email) {
            var url = Api.makeUrl('/incontrol/client_service/%s/email/delete', [service.id]);

            return Api.sendAndDispatchPromise(Constants.api.DELETE_SERVICE_EMAIL, 'POST', url, null, {
                email_address: email
            }, { service: service });
        },

        diagnostics: function (id) {
            const url = Api.makeUrl('/service/%d/diagnostics', [id]);

            return Api.sendPromise(null, 'POST', url);
        },

        /**
         * Retrieves all services for a given client ID.
         */
        getServices: function (clientId) {
            var url = Api.makeUrl('/incontrol/client/%s/services', [clientId]);

            Api.send(Constants.api.GET_SERVICES, 'GET', url, null, {}, { clientId: clientId });
        },

        /**
         * Retrieves the details for a vision service
         */
        getVisionServiceDetails: function (serviceId) {
            var url = Api.makeUrl('/vision/service/%s/details', [serviceId]);

            return Api.sendPromise(null, 'POST', url);
        },

        outdated: function(clientId) {
            var url = Api.makeUrl('/client/%d/outdated', [clientId]);

            return Api.sendPromise(Constants.api.CLIENT_OUTDATED, 'GET', url);
        },

        buildServicesReportUrl: function (clientId) {
            const hmac = UserStore.getHmac();

            return url = Api.makeUrl('/client/%s/services.xlsx?authorization=%s&x-hmac-expires=%s', [
                clientId,
                encodeURIComponent(buildHmacString(hmac)),
                encodeURIComponent(hmac.expires)
            ]);
        },

        /**
         * Upgrades a service to a new plan.
         *
         * Does not dispatch.
         */
        upgrade: function (serviceId, offerId, card, token, tokenCvn, invoice, newContract = false, couponCode) {
            var url = Api.makeUrl('/service/%d/upgrade/%d', [serviceId, offerId]);

            return Api.sendPromise(Constants.api.SERVICE_UPGRADE, 'POST', url, null, {
                card,
                couponCode,
                keepCoupon: typeof couponCode === 'undefined',
                invoice,
                newContract,
                token,
                tokenCvn,
            });
        },

        upgradeOptions: function (serviceId, couponCode) {
            var url = Api.makeUrl('/service/%d/upgrade', [serviceId]);

            return Api.sendPromise(Constants.api.SERVICE_UPGRADE_OPTIONS, 'POST', url, null, {
              couponCode,
            });
        }
    },

    /**
     * Coupon code checks
     */
    coupon: {
        getCoupon: function (serviceType, code) {
            var url = Api.makeUrl('/signup/coupon-check/%s/%s', [serviceType, code]);

            return Api.sendPromise(Constants.api.CHECK_COUPON, 'GET', url, null, null, {Authorization: ''});
        }
    },

    domain: {
        getDomains: function (clientId) {
            var url = Api.makeUrl('/client/%s/domains', [clientId]);

            Api.send(Constants.api.DOMAIN_LIST, 'GET', url);
        }
    },

    dns: {
        checkClusterHost: function (host) {
            const url = Api.makeUrl('/dns/host/check');

            return Api.sendPromise(Constants.api.DNS_CHECK_HOST, 'POST', url, null, {
                host
            })
        },

        getPtr: function (ipAddress) {
            const url = Api.makeUrl('/dns/name/%s', [ipAddress]);

            return Api.sendPromise(Constants.api.DNS_PTR, 'GET', url);
        }
    },

    hosting: {
        checkPassword: function (password) {
            const url = Api.makeUrl('/hosting/cpanel/password');

            return Api.sendPromise(Constants.api.HOSTING_CHECK_PASSWORD, 'POST', url, null, {
                password,
            });
        },

        getAutoLoginUrl: function (clientServiceId) {
            const _hmac = UserStore.getHmac();

            const url = Api.makeUrl('/hosting/cpanel/%s/autologinurl?authorization=%s&x-hmac-expires=%s', [
                clientServiceId,
                encodeURIComponent(buildHmacString(_hmac)),
                encodeURIComponent(_hmac.expires),
            ]);

            return Api.sendPromise(Constants.api.GET_CPANEL_LOGIN_URL, 'GET', url, );
        },

        getWebsites: function (clientId) {
            const url = Api.makeUrl('/client/%s/websites', [clientId]);

            Api.send(Constants.api.WEBSITE_LIST, 'GET', url);
        }
    },

    /**
     * Invoice related API calls
     */
    invoice: {
        /**
         * Assigns a payment to an invoice.
         */
        assignPayment: function (invoice, paymentId, amount) {
            var url = Api.makeUrl('/invoice/%s/assign/%s', [invoice.id, paymentId]);

            return Api.sendPromise(Constants.api.INVOICE_PAY, 'POST', url, null, {
                amount: amount
            });
        },

        /**
         * Builds a URL to a pdf that we can send the browser to to download.
         */
        buildLegacyPdfUrl: function (invoice) {
            var _hmac = UserStore.getHmac();

            return url = Api.makeUrl('/incontrol/client/%s/invoice/%s?_format=pdf&authorization=%s&x-hmac-expires=%s', [
                invoice.billing_group.client_id,
                invoice.id,
                encodeURIComponent(buildHmacString(_hmac)),
                encodeURIComponent(_hmac.expires)
            ]);
        },

        /**
         * Builds a URL to a pdf that we can send the browser to to download.
         */
        buildPdfUrl: function (invoice) {
            var _hmac = UserStore.getHmac();

            return url = Api.makeUrl('/invoice/%s.pdf?authorization=%s&x-hmac-expires=%s', [
                invoice.id,
                encodeURIComponent(buildHmacString(_hmac)),
                encodeURIComponent(_hmac.expires)
            ]);
        },

        /**
         * Loads a filtered list of invoices.
         */
        getFilteredLegacyInvoices: function (pageNumber) {
            var clientId = UserStore.getClientId();
            var url = Api.makeUrl('/incontrol/client/%s/invoices_filtered', [clientId]);

            Api.send(Constants.api.GET_FILTERED_LEGACY_INVOICES, 'POST', url, null, { page_number: pageNumber });
        },

        /**
         * Sends a request to retrieve the last 30 invoices.
         */
        getInvoices: function (pageNumber, options) {
            var clientId = UserStore.getClientId();
            var url = Api.makeUrl('/client/%s/invoices', [clientId]);

            Api.send(Constants.api.GET_INVOICES, 'POST', url, null, Object.assign({}, options, {
                offset: pageNumber === null ? null : Constants.RPP * (pageNumber - 1),
                limit: pageNumber === null ? null : Constants.RPP
            }));
        }
    },

    partner: {
        getPartner(partnerHash) {
            return Api.sendPromise(Constants.api.PARTNER_GET, 'GET', Api.makeUrl('/partner/%s', [partnerHash]))
        },

        authorise(partnerId, clientId) {
          const url = Api.makeUrl(`/partner/${partnerId}`)

          return Api.sendPromise(Constants.api.PARTNER_AUTH, 'PUT', url, {}, {
            clientId,
          })
        },

        deauthorise(partnerId, clientId) {
          const url = Api.makeUrl(`/partner/${partnerId}`)

          return Api.sendPromise(Constants.api.PARTNER_DEAUTH, 'DELETE', url, {}, {
            clientId,
          })
        },

        services() {
          const url = Api.makeUrl(`/user/partner/services`)

          return Api.sendPromise(null, 'GET', url);
        }
    },

    plan: {
        /**
         * Retrieve plans for a service type.
         *
         *
         * Does not dispatch.
         */
        getPlans: function (serviceType) {
            var url = Api.makeUrl('/incontrol/plan/%s', [serviceType]);

            return Api.sendPromise(Constants.api.GET_PLANS, 'GET', url, null, null, { Authorization: '' });
        }
    },

    signup: {
        checkEmail: function (email) {
            var url = Api.makeUrl('/user/check');

            var headers = {};
            if (!UserStore.getClientId()) {
                headers.Authorization = '';
            }

            return Api.sendPromise(Constants.api.SIGNUP_CHECK_EMAIL, 'POST', url, null, {
                username: email
            }, headers);
        },

        createSignup: function (signup, plan, coupon, billingGroup) {
            const groupSuffix = billingGroup ?
                vsprintf('/%s', [billingGroup.id]) :
                '';
            var url = Api.makeUrl('/signup/create/%s%s', [plan.id, groupSuffix]);

            var query = {};
            if (coupon) {
                query.coupon = coupon;
            }
            var headers = {};
            if (!billingGroup) {
                headers.Authorization = '';
            }

            return Api.sendPromise(Constants.api.SIGNUP_CREATE, 'POST', url, query, signup, headers);
        },

        paySignupCard: function (signupId, signupKey, card) {
            var url = Api.makeUrl('/signup/%s/pay', [signupId]);

            card.number = encryptValue(card.number, Config.eCryptKey);
            card.cvn = encryptValue(card.cvn, Config.eCryptKey);

            return Api.sendPromise(Constants.api.SIGNUP_PAY, 'POST', url, null, { card }, {
                Authorization: vsprintf('SIGNUP %s:%s', [signupId, signupKey])
            });
        },

        paySignupToken: function (signupId, signupKey, token, cvn) {
            var url = Api.makeUrl('/signup/%s/pay_token/%s', [signupId, token.uuid]);

            cvn = encryptValue(cvn, Config.eCryptKey);

            return Api.sendPromise(Constants.api.SIGNUP_PAY, 'POST', url, null, { cvn }, {
                Authorization: vsprintf('SIGNUP %s:%s', [signupId, signupKey])
            });
        },

        processSignup: function (signupId, signupKey) {
            var url = Api.makeUrl('/signup/%s/process', [signupId]);

            return Api.sendPromise(Constants.api.SIGNUP_PROCESS, 'POST', url, null, {}, {
                Authorization: vsprintf('SIGNUP %s:%s', [signupId, signupKey])
            });
        }
    },

    suppressedEmails: {
        getSuppressedEmails: function (clientId) {
            const url = Api.makeUrl('/emailsuppressions/%s', [clientId]);

            return Api.send(Constants.api.SUPPRESSED_EMAILS_LIST, 'GET', url);
        },

        updateSuppressedEmails: function (clientId, suppressions) {
            const url = Api.makeUrl('/emailsuppressions/%s', [clientId]);

            return Api.send(Constants.api.SUPPRESSED_EMAILS_UPDATE, 'PUT', url, {}, { suppressions });
        }
    }
};
module.exports = Invocation;



// WEBPACK FOOTER //
// ./app/api/Invocation.js